import { useQuery } from '@tanstack/react-query';
import { requestDownloadUrl } from '../../shared/api/document-queries.js';

/**
 * A list-row thumbnail.
 *
 * Previews live behind signed URLs, so each needs a short-lived link. Two
 * things keep that from being expensive:
 *
 *  - the query is cached for slightly less than the URL's lifetime, so
 *    scrolling back over a row reuses the link rather than minting another;
 *  - the image is `loading="lazy"`, so a virtualised row that renders just
 *    outside the viewport never fetches at all.
 *
 * Preview links are deliberately not audited, they load on every render, and
 * burying the trail in thumbnail fetches would make it unreadable. Originals
 * are audited every time.
 */

const PREVIEW_TTL_SECONDS = 600;

export interface DocumentThumbnailProps {
  readonly organizationId: string;
  readonly documentId: string;
  readonly hasPreview: boolean;
  readonly mimeType: string | null;
}

export function DocumentThumbnail({
  organizationId,
  documentId,
  hasPreview,
  mimeType,
}: DocumentThumbnailProps) {
  const query = useQuery({
    queryKey: ['document-preview', organizationId, documentId],
    queryFn: () =>
      requestDownloadUrl(organizationId, documentId, {
        preview: true,
        expiresInSeconds: PREVIEW_TTL_SECONDS,
      }),
    enabled: hasPreview,
    staleTime: (PREVIEW_TTL_SECONDS - 60) * 1000,
    retry: false,
  });

  if (!hasPreview || query.isError) {
    return (
      <div
        aria-hidden="true"
        className="flex size-12 shrink-0 items-center justify-center rounded border border-border bg-surface-sunken text-[10px] font-semibold uppercase text-content-subtle"
      >
        {extensionLabel(mimeType)}
      </div>
    );
  }

  return (
    <div className="size-12 shrink-0 overflow-hidden rounded border border-border bg-surface-sunken">
      {query.data === undefined ? null : (
        <img
          src={query.data.downloadUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
        />
      )}
    </div>
  );
}

function extensionLabel(mimeType: string | null): string {
  if (mimeType === null) return '?';
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType === 'text/csv') return 'CSV';
  if (mimeType.includes('spreadsheet')) return 'XLSX';
  if (mimeType.startsWith('image/')) return mimeType.slice(6, 10).toUpperCase();
  return '?';
}
